/*============================================================================
  New Search  —  full-screen predictive search panel
  - Custom element <new-search>
  - Opened by any [data-new-search-open] element OR the theme's existing
    header search summary/icon (latched in capture phase)
  - Fetches /search/suggest rendered through sections/new-search.liquid
  - No dependency on the theme's stock predictive-search.js

  Rev 2026-09-10 — fixes:
    1. DOM refs resolved in connectedCallback (not constructor); no hard
       permanent-bail — native search always left working.
    2. open(): synchronous focus kept for the iOS keyboard, but a forced
       style flush is done first so the panel (visibility:hidden -> visible)
       is actually focusable in the same gesture tick.
    3. announce(): real result count (products + suggestions, or 0 when the
       fragment carries .ns__noresults / data-ns-count) — the old
       [data-ns-product] probe always returned 0, so every search read out
       "No results".
    4. Loading state can no longer stick: request-token + finally; an 8s
       timeout now surfaces the error instead of a permanent dimmed pane.
    5. Response cache is FIFO-capped (30) — no unbounded growth per session.
    6. Singleton guard: a 2nd placement (section is enabled_on header AND
       footer groups) stays inert instead of double-binding document
       listeners and opening a second panel.
    7. --ns-top re-measured on resize while open.
============================================================================*/
(function () {
  'use strict';

  var SELECTORS = {
    panel: '.ns__panel',
    form: '.ns__form',
    input: '.ns__input',
    body: '.ns__body',
    results: '.ns__results',
    live: '.ns__status',
  };

  var OPEN_CLASS = 'ns--open'; // on <new-search>
  var ACTIVE_CLASS = 'ns--querying'; // typing state (results visible, empty hidden)
  var LOADING_CLASS = 'ns--loading';
  var BODY_OPEN_CLASS = 'ns-scroll-lock';
  var CACHE_MAX = 30;
  var FETCH_TIMEOUT_MS = 8000;

  class NewSearch extends HTMLElement {
    constructor() {
      super();
      // primitives / state only — DOM is queried in connectedCallback
      this.minChars = 1;
      this.debounceMs = 200;
      this.sectionId = 'new-search';
      this.productLimit = 4;
      this.queryLimit = 6;

      this.isOpen = false;
      this.inert = false;
      this.term = '';
      this.cache = Object.create(null);
      this._cacheKeys = [];
      this._reqToken = 0;
      this.abort = null;
      this.debounceTimer = null;
      this.lastFocusedBeforeOpen = null;
      this.activeIndex = -1;

      this.onInput = this.onInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onDocKeydown = this.onDocKeydown.bind(this);
      this.onDocPointerDown = this.onDocPointerDown.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      // Section is enabled on the header AND footer groups — if a merchant
      // adds it to both, keep every instance after the first inert.
      if (NewSearch._instance && NewSearch._instance !== this) {
        this.inert = true;
        return;
      }
      NewSearch._instance = this;

      this.panel = this.querySelector(SELECTORS.panel);
      this.form = this.querySelector(SELECTORS.form);
      this.input = this.querySelector(SELECTORS.input);
      this.bodyEl = this.querySelector(SELECTORS.body);
      this.resultsEl = this.querySelector(SELECTORS.results);
      this.liveEl = this.querySelector(SELECTORS.live);

      this.minChars = parseInt(this.dataset.minChars, 10) || 1;
      this.debounceMs = parseInt(this.dataset.debounce, 10) || 200;
      this.sectionId = this.dataset.sectionId || 'new-search';
      this.productLimit = parseInt(this.dataset.productLimit, 10) || 4;
      this.queryLimit = parseInt(this.dataset.queryLimit, 10) || 6;

      // Cart icon count — kept live off cart-change signals instead of
      // hooking each cart-mutating module individually: the theme's
      // built-in cart-update pub/sub (PDP form, etc.) AND the custom
      // cart drawer's own 'new-cart:updated' DOM event (new-cart.js is
      // intentionally self-contained and never touches that pub/sub).
      this.cartCountEl = this.querySelector('[data-ns-cart-count]');
      if (this.cartCountEl) {
        var refreshCartCount = () => {
          var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          fetch(root.replace(/\/$/, '') + '/cart.js')
            .then((r) => r.json())
            .then((cart) => {
              if (!this.cartCountEl) return;
              this.cartCountEl.textContent = cart.item_count;
            })
            .catch(() => {});
        };

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this._cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, refreshCartCount);
        }

        this._onNewCartUpdated = refreshCartCount;
        document.addEventListener('new-cart:updated', this._onNewCartUpdated);
      }

      // Latch the theme's existing header search control. This works even if
      // the panel markup is incomplete, so the native modal is never left
      // orphaned — the open() call below no-ops safely if refs are missing.
      this._triggerHandler = (e) => {
        if (e.button && e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        var t = e.target;
        if (!t || typeof t.closest !== 'function') return;
        var trigger = t.closest(
          '[data-new-search-open], .header__search summary, summary.header__icon--search'
        );
        if (!trigger) return;
        e.preventDefault();
        e.stopPropagation();
        var details = trigger.closest('details');
        if (details) details.removeAttribute('open');
        this._lastTrigger = trigger;
        this.open();
      };
      document.addEventListener('click', this._triggerHandler, true);
      document.addEventListener('keydown', this.onDocKeydown);
      document.addEventListener('pointerdown', this.onDocPointerDown, true);

      if (!this.panel || !this.input || !this.form) {
        // Panel shell missing — trigger still swallowed above so we don't get
        // the legacy modal, but there is nothing to open. Bail out of the
        // rest of the wiring.
        if (window.console) console.warn('[new-search] panel markup missing');
        return;
      }

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown);
      this.form.addEventListener('submit', this.onFormSubmit);

      this.querySelectorAll('[data-new-search-close]').forEach((el) =>
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        })
      );

      this.bindRails(this);
    }

    disconnectedCallback() {
      if (this.inert) return;
      if (NewSearch._instance === this) NewSearch._instance = null;
      document.removeEventListener('keydown', this.onDocKeydown);
      document.removeEventListener('pointerdown', this.onDocPointerDown, true);
      if (this._triggerHandler) {
        document.removeEventListener('click', this._triggerHandler, true);
      }
      window.removeEventListener('resize', this.onResize);
      clearTimeout(this.debounceTimer);
      if (this.abort) this.abort.abort();
      if (this._cartUpdateUnsub) this._cartUpdateUnsub();
      if (this._onNewCartUpdated) document.removeEventListener('new-cart:updated', this._onNewCartUpdated);
      this.unlockScroll();
    }

    // Close when the shopper points at the visible page beside / below the panel.
    onDocPointerDown(e) {
      if (!this.isOpen) return;
      var t = e.target;
      if (this.panel && t && this.panel.contains(t)) return;
      if (
        t &&
        typeof t.closest === 'function' &&
        t.closest('[data-new-search-open], .header__search, .header__icon--search')
      ) {
        return;
      }
      this.close();
    }

    onResize() {
      if (this.isOpen) this.syncTopOffset();
    }

    /* ---------- open / close --------------------------------------------- */

    open() {
      if (this.isOpen || !this.panel || !this.input) return;
      this.isOpen = true;
      this.lastFocusedBeforeOpen = document.activeElement;
      this.syncTopOffset(); // measure BEFORE locking scroll
      this.classList.add(OPEN_CLASS);
      this.setAttribute('open', '');
      this.lockScroll();
      window.addEventListener('resize', this.onResize);

      // Force the just-applied `visibility: visible` to resolve so the input
      // is focusable in THIS synchronous (user-gesture) tick — that is what
      // lets iOS Safari raise the keyboard.
      void this.panel.offsetWidth;
      try {
        this.input.focus({ preventScroll: true });
      } catch (_) {
        this.input.focus();
      }

      document.dispatchEvent(new CustomEvent('new-search:open'));
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove(OPEN_CLASS);
      this.removeAttribute('open');
      this.unlockScroll();
      window.removeEventListener('resize', this.onResize);

      this._reqToken++; // orphan any in-flight response
      if (this.abort) this.abort.abort();
      clearTimeout(this.debounceTimer);

      if (this.input) this.input.value = '';
      this.term = '';
      this.showEmpty();

      if (
        this.lastFocusedBeforeOpen &&
        typeof this.lastFocusedBeforeOpen.focus === 'function'
      ) {
        this.lastFocusedBeforeOpen.focus();
      }
      document.dispatchEvent(new CustomEvent('new-search:close'));
    }

    // Anchor the panel to the TOP edge of the sticky header so it sits over
    // the nav row with the announcement bar still showing above. Clamped so a
    // mis-measured wrapper can never push it far down the page.
    syncTopOffset() {
      var top = 0;
      var header =
        document.querySelector('.section-header') ||
        document.querySelector('sticky-header') ||
        document.querySelector('.header-wrapper') ||
        (this._lastTrigger &&
          this._lastTrigger.closest &&
          this._lastTrigger.closest('.shopify-section, header'));
      if (header && header.getBoundingClientRect) {
        var t = header.getBoundingClientRect().top;
        if (t >= 0 && t < 240) top = Math.round(t);
      }
      this.style.setProperty('--ns-top', top + 'px');
    }

    lockScroll() {
      if (document.body.classList.contains(BODY_OPEN_CLASS)) return;
      this._lockedScroll = true;
      this._scrollY = window.scrollY;
      document.body.classList.add(BODY_OPEN_CLASS);
      document.body.style.top = '-' + this._scrollY + 'px';
    }

    unlockScroll() {
      if (!this._lockedScroll) return;
      this._lockedScroll = false;
      document.body.classList.remove(BODY_OPEN_CLASS);
      document.body.style.top = '';
      if (typeof this._scrollY === 'number') window.scrollTo(0, this._scrollY);
    }

    /* ---------- input handling ------------------------------------------ */

    onInput() {
      var value = this.input.value.trim();

      if (value === this.term) return;
      this.term = value;

      if (value.length < this.minChars) {
        clearTimeout(this.debounceTimer);
        this._reqToken++;
        if (this.abort) this.abort.abort();
        this.showEmpty();
        return;
      }

      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.run(value), this.debounceMs);
    }

    showEmpty() {
      this.classList.remove(ACTIVE_CLASS, LOADING_CLASS);
      if (this.resultsEl) this.resultsEl.innerHTML = '';
      if (this.bodyEl) this.bodyEl.scrollTop = 0;
      this.activeIndex = -1;
    }

    /* ---------- fetch -------------------------------------------------- */

    buildUrl(term) {
      var limit = Math.min(10, Math.max(1, this.productLimit, this.queryLimit));
      var params = new URLSearchParams({
        q: term,
        section_id: this.sectionId,
        'resources[type]': 'query,product',
        'resources[limit]': String(limit),
        'resources[options][unavailable_products]': 'last',
      });
      var root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return root.replace(/\/$/, '') + '/search/suggest?' + params.toString();
    }

    cachePut(term, html) {
      if (!(term in this.cache)) {
        this._cacheKeys.push(term);
        if (this._cacheKeys.length > CACHE_MAX) {
          var old = this._cacheKeys.shift();
          delete this.cache[old];
        }
      }
      this.cache[term] = html;
    }

    run(term) {
      if (term !== this.input.value.trim()) return;

      if (this.cache[term]) {
        this.render(this.cache[term], term);
        return;
      }

      if (this.abort) this.abort.abort();
      var ctrl = new AbortController();
      this.abort = ctrl;
      var token = ++this._reqToken;
      this.classList.add(LOADING_CLASS, ACTIVE_CLASS);

      var timeout = setTimeout(() => {
        ctrl._timedOut = true;
        ctrl.abort();
      }, FETCH_TIMEOUT_MS);

      fetch(this.buildUrl(term), { signal: ctrl.signal })
        .then((r) => {
          if (!r.ok) throw new Error('Search ' + r.status);
          return r.text();
        })
        .then((html) => {
          this.cachePut(term, html);
          this.render(html, term);
        })
        .catch((err) => {
          // A plain AbortError means a newer keystroke superseded this one —
          // stay silent. A timed-out abort is a real failure.
          if (err && err.name === 'AbortError' && !ctrl._timedOut) return;
          if (token === this._reqToken && this.resultsEl) {
            this.resultsEl.innerHTML =
              '<p class="ns__error" role="alert">' +
              (this.dataset.errorText || 'Something went wrong. Please try again.') +
              '</p>';
            this.classList.add(ACTIVE_CLASS);
          }
        })
        .finally(() => {
          clearTimeout(timeout);
          if (token === this._reqToken) this.classList.remove(LOADING_CLASS);
        });
    }

    render(html, term) {
      if (term !== this.input.value.trim()) return; // still current?
      if (!this.resultsEl) return;

      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('#new-search-results');

      this.resultsEl.innerHTML = fresh ? fresh.innerHTML : '';
      this.classList.remove(LOADING_CLASS);
      this.classList.add(ACTIVE_CLASS);
      if (this.bodyEl) this.bodyEl.scrollTop = 0;
      this.bindRails(this.resultsEl);
      // product cards are the real card-newproduct snippet -> bind its
      // carousel/swatch engine (assets/new-collection.js), same component
      // used on the Collection page and /search results page.
      if (window.NCG && window.NCG.initCards) window.NCG.initCards(this.resultsEl);
      this.activeIndex = -1;

      this.announce(this.resultCount(fresh), term);
    }

    // Real hit count for the live region. Prefers the server's data-ns-count
    // (counts real predictive-search matches only), but that attribute reads
    // 0 for a zero-match term even though the fragment still renders fallback
    // product cards (settings.ns_fallback_collection) — so a 0 there falls
    // through to counting what's actually in the DOM, instead of announcing
    // "No results" while fallback products are visibly on screen.
    resultCount(fresh) {
      var domCount = 0;
      if (this.resultsEl) {
        var products = this.resultsEl.querySelectorAll('.ncg-card').length;
        var suggestions = this.resultsEl.querySelectorAll(
          '.ns__group--suggestions .ns__list-item'
        ).length;
        domCount = products + suggestions;
      }
      if (fresh && fresh.hasAttribute('data-ns-count')) {
        var n = parseInt(fresh.getAttribute('data-ns-count'), 10);
        n = Number.isFinite(n) ? n : 0;
        return n > 0 ? n : domCount;
      }
      return domCount;
    }

    // Click-drag ("grab & swipe") for the horizontal rails.
    //  - pointer down + move: scrollLeft tracks the cursor 1:1 (no lag)
    //  - dragging past either end pulls the whole strip with rubber-band
    //    resistance (CSS transform), which springs back when you let go
    //  - releasing mid-rail projects a flick glide that eases to a stop;
    //    flicking into an end gives a soft bounce
    //  Touch is left alone — the browser's own momentum + overscroll are better.
    bindRails(root) {
      var rails = (root || this).querySelectorAll('[data-ns-rail]');
      rails.forEach((rail) => {
        if (rail._nsRail) return;
        rail._nsRail = true;

        var RUBBER = 150; // asymptotic max px the strip can be pulled past an edge
        var dragging = false;
        var raf = 0;
        var rbTO = 0; // spring-back timeout
        var rubberT = 0; // translateX currently applied to the strip
        var pendingBounce = 0; // overshoot captured by the glide, spent on settle
        var target = 0;
        var startScroll = 0;
        var startX = 0;
        var lastX = 0;
        var lastT = 0;
        var vel = 0; // pointer px / ms
        var moved = false;

        var maxScroll = () => rail.scrollWidth - rail.clientWidth;
        // diminishing resistance — approaches RUBBER, never reaches it
        var resist = (o) => (RUBBER * o) / (o + RUBBER);
        var setRubber = (px) => {
          rubberT = px;
          rail.style.transform = px ? 'translate3d(' + px + 'px,0,0)' : '';
        };
        var springHome = () => {
          clearTimeout(rbTO);
          rail.style.transition = 'transform .52s cubic-bezier(.22,1,.36,1)';
          setRubber(0);
          rbTO = setTimeout(() => {
            rail.style.transition = '';
            rail.style.willChange = '';
          }, 540);
        };

        var tick = () => {
          raf = 0;
          var max = maxScroll();
          if (target < 0) {
            if (!pendingBounce) pendingBounce = target;
            target = 0;
          } else if (target > max) {
            if (!pendingBounce) pendingBounce = target - max;
            target = max;
          }

          var cur = rail.scrollLeft;
          var next = cur + (target - cur) * 0.2;
          rail.scrollLeft = next;

          if (Math.abs(target - next) > 0.4) {
            raf = requestAnimationFrame(tick);
          } else {
            rail.scrollLeft = target;
            rail.classList.remove('ns--dragging');
            if (pendingBounce && !rubberT) {
              var px =
                (pendingBounce < 0 ? 1 : -1) * resist(Math.abs(pendingBounce) * 0.5);
              rail.style.willChange = 'transform';
              rail.style.transition = 'transform .15s ease-out';
              setRubber(px);
              clearTimeout(rbTO);
              rbTO = setTimeout(springHome, 150);
            } else {
              rail.style.willChange = '';
            }
            pendingBounce = 0;
          }
        };
        var run = () => {
          if (!raf) raf = requestAnimationFrame(tick);
        };

        rail.addEventListener('pointerdown', (e) => {
          if (e.pointerType === 'touch') return; // native touch scrolling
          if (e.button && e.button !== 0) return;
          if (maxScroll() <= 0) return; // nothing to scroll — no drag, no rubber
          dragging = true;
          moved = false;
          startX = lastX = e.clientX;
          startScroll = rail.scrollLeft;
          target = rail.scrollLeft;
          lastT = e.timeStamp;
          vel = 0;
          pendingBounce = 0;
          if (raf) { cancelAnimationFrame(raf); raf = 0; }
          clearTimeout(rbTO);
          rail.style.transition = 'none';
          rail.style.willChange = 'transform';
          rail.classList.add('ns--dragging');
          try {
            rail.setPointerCapture(e.pointerId);
          } catch (_) {}
        });

        rail.addEventListener('pointermove', (e) => {
          if (!dragging) return;
          var dx = e.clientX - startX;
          if (Math.abs(dx) > 3) moved = true;
          var max = maxScroll();
          var raw = startScroll - dx;
          if (raw < 0) {
            rail.scrollLeft = 0;
            setRubber(resist(-raw)); // pulled past the start — stretch right
          } else if (raw > max) {
            rail.scrollLeft = max;
            setRubber(-resist(raw - max)); // pulled past the end — stretch left
          } else {
            rail.scrollLeft = raw; // 1:1 with the pointer, no easing
            if (rubberT) setRubber(0);
          }
          target = rail.scrollLeft;
          var dt = e.timeStamp - lastT;
          if (dt > 0) {
            var inst = (e.clientX - lastX) / dt;
            vel = vel * 0.8 + inst * 0.2;
          }
          lastX = e.clientX;
          lastT = e.timeStamp;
        });

        var end = (e) => {
          if (!dragging) return;
          dragging = false;
          rail.classList.remove('ns--dragging');
          try {
            rail.releasePointerCapture(e.pointerId);
          } catch (_) {}
          if (rubberT) {
            springHome(); // let go while stretched — snap back
          } else {
            rail.style.transition = 'none';
            target = rail.scrollLeft - vel * 340; // flick projection, eased by tick
            run();
          }
          if (moved) {
            var kill = (ev) => {
              ev.preventDefault();
              ev.stopPropagation();
            };
            rail.addEventListener('click', kill, true);
            setTimeout(() => rail.removeEventListener('click', kill, true), 60);
          }
        };

        rail.addEventListener('pointerup', end);
        rail.addEventListener('pointercancel', end);
        rail.addEventListener('lostpointercapture', end);
        rail.addEventListener('dragstart', (e) => e.preventDefault());
      });
    }

    announce(count, term) {
      if (!this.liveEl) return;
      var tmpl = count
        ? this.dataset.resultsText || '[count] results for "[terms]"'
        : this.dataset.noResultsText || 'No results for "[terms]"';
      // function replacer for [terms] so a "$" in the query is not treated as
      // a String.replace special pattern
      this.liveEl.textContent = tmpl
        .replace(/\[count\]/g, String(count))
        .replace(/\[terms\]/g, () => term);
    }

    /* ---------- keyboard --------------------------------------------- */

    get optionEls() {
      // product cards are the real card-newproduct snippet (shared with
      // Collection / the /search page) -> it has no [data-ns-option] of its
      // own, so its info link (title+price, tabindex="-1" like our other
      // options) is included here instead of adding search-only attributes
      // to a snippet used elsewhere.
      return Array.from(
        this.querySelectorAll('[data-ns-option], .ns__cards-item .ncg-card__info')
      ).filter((el) => el.offsetParent !== null);
    }

    onKeydown(e) {
      var opts = this.optionEls;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!opts.length) return;
        e.preventDefault();
        var dir = e.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = (this.activeIndex + dir + opts.length) % opts.length;
        opts.forEach((el, i) => {
          var on = i === this.activeIndex;
          el.classList.toggle('ns__option--active', on);
          if (on) {
            el.setAttribute('aria-selected', 'true');
            el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          } else {
            el.removeAttribute('aria-selected');
          }
        });
      } else if (e.key === 'Enter') {
        var current = opts[this.activeIndex];
        if (current && current.tagName === 'A') {
          e.preventDefault();
          current.click();
        }
      }
    }

    onDocKeydown(e) {
      if (!this.isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      } else if (e.key === 'Tab') {
        this.trapFocus(e);
      }
    }

    trapFocus(e) {
      if (!this.panel) return;
      var focusables = this.panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      var list = Array.from(focusables).filter((el) => el.offsetParent !== null);
      if (!list.length) return;
      var first = list[0];
      var last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    onFormSubmit(e) {
      var value = this.input.value.trim();
      if (!value) {
        e.preventDefault();
        this.input.focus();
      }
      // otherwise let the native GET /search?q=... submit through
    }
  }

  if (!customElements.get('new-search')) {
    customElements.define('new-search', NewSearch);
  }
})();
